import React, { useState } from "react";
import { useSelector } from "react-redux";
import i18n from "i18next";
import MultiInputItemEditor from "./MultiInputItemEditor";

function randomId() {
  return Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10);
}

function getSectionSiblingScenes(state, boxId) {
  const boxes = state?.undoGroup?.present?.boxesById || {};
  const navItemsById = state?.undoGroup?.present?.navItemsById || {};
  const box = boxes[boxId];
  if (!box) return [];
  const sceneId = box.parent;
  const scene = navItemsById[sceneId];
  if (!scene) return [];
  const parent = navItemsById[scene.parent];
  if (!parent || parent.type !== "section") {
    return [sceneId];
  }
  return (parent.children || []).filter((id) => {
    const item = navItemsById[id];
    return item && item.type === "slide";
  });
}

export default function GuideMultiInputProperties({ boxId, state, onToolbarUpdated }) {
  const items = Array.isArray(state && state.items) ? state.items : [];
  const [collapsedMap, setCollapsedMap] = useState({});
  const siblings = useSelector((s) => getSectionSiblingScenes(s, boxId));
  const navItemsById = useSelector((s) => s?.undoGroup?.present?.navItemsById || {});
  const viewToolbarsById = useSelector((s) => s?.undoGroup?.present?.viewToolbarsById || {});

  const update = (newItems) => onToolbarUpdated(boxId, "main", "state", "items", newItems);

  const cue = {
    beacon: true,
    cursor: true,
    backdrop: false,
    ...((state && state.guidedCue) || {}),
  };
  const autoAdvance = !state || state.advanceOnComplete !== false;
  const setField = (field, value) => onToolbarUpdated(boxId, "main", "state", field, value);
  const setCue = (key, value) => setField("guidedCue", { ...cue, [key]: value });

  const behaviorRow = (labelKey, fallback, checked, onChange) => (
    <label
      style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#374151" }}
    >
      <input type="checkbox" checked={checked} onChange={onChange} aria-label={labelKey} />
      {i18n.t(labelKey) || fallback}
    </label>
  );

  const onItemChange = (idx, updated) => {
    const n = items.slice();
    n[idx] = updated;
    update(n);
  };
  const onDelete = (idx) => {
    const n = items.slice();
    const removedId = n[idx] && n[idx].id;
    n.splice(idx, 1);
    update(n);
    if (removedId) {
      setCollapsedMap((m) => {
        const next = { ...m };
        delete next[removedId];
        return next;
      });
    }
  };
  const onMoveUp = (idx) => {
    if (idx <= 0) return;
    const n = items.slice();
    [n[idx - 1], n[idx]] = [n[idx], n[idx - 1]];
    update(n);
  };
  const onMoveDown = (idx) => {
    if (idx >= items.length - 1) return;
    const n = items.slice();
    [n[idx + 1], n[idx]] = [n[idx], n[idx + 1]];
    update(n);
  };

  const toggleItem = (id) => {
    setCollapsedMap((m) => ({ ...m, [id]: !m[id] }));
  };

  const allCollapsed = items.length > 0 && items.every((it) => collapsedMap[it.id]);
  const anyExpanded = items.some((it) => !collapsedMap[it.id]);

  const collapseAll = () => {
    const map = {};
    for (const it of items) map[it.id] = true;
    setCollapsedMap(map);
  };
  const expandAll = () => setCollapsedMap({});

  const addItem = (kind) => {
    const offset = items.length * 3;
    const newItem = {
      id: randomId(),
      kind,
      unit: "%",
      x: 10 + (offset % 70),
      y: 10 + (offset % 70),
      width: kind === "tag" ? 20 : 25,
      height: kind === "click" ? 8 : 5,
      label: "",
      color: kind === "click" ? "#ff00a0" : kind === "tag" ? "#a855f7" : "#3b82f6",
    };
    if (kind === "click" || kind === "input") {
      newItem.onClickAction = "next";
      newItem.targetStepId = null;
    }
    if (kind === "input") {
      newItem.inputType = "text";
      newItem.validation = { mode: "random" };
    }
    if (kind === "tag") {
      newItem.tagRef = "";
    }
    update(items.concat([newItem]));
  };

  return (
    <div
      className="guide-mi-props"
      style={{ padding: 12, display: "flex", flexDirection: "column", gap: 12 }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            textTransform: "uppercase",
            color: "#374151",
          }}
        >
          {i18n.t("GuideMultiInput.behavior_title") || "Behavior"}
        </div>
        {behaviorRow(
          "GuideMultiInput.auto_advance",
          "Advance scene when all steps done",
          autoAdvance,
          (e) => setField("advanceOnComplete", e.target.checked)
        )}
        {behaviorRow(
          "GuideMultiInput.cue_beacon",
          "Pulsing highlight on active step",
          cue.beacon,
          (e) => setCue("beacon", e.target.checked)
        )}
        {behaviorRow(
          "GuideMultiInput.cue_cursor",
          "Guide cursor moves to active step",
          cue.cursor,
          (e) => setCue("cursor", e.target.checked)
        )}
        {behaviorRow(
          "GuideMultiInput.cue_backdrop",
          "Dim screen around active step",
          cue.backdrop,
          (e) => setCue("backdrop", e.target.checked)
        )}
        {behaviorRow(
          "GuideMultiInput.zoom_enabled",
          "Zoom to the active step",
          state && state.zoomEnabled === true,
          (e) => setField("zoomEnabled", e.target.checked)
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          style={{
            flex: 1,
            fontSize: 12,
            fontWeight: 700,
            textTransform: "uppercase",
            color: "#374151",
          }}
        >
          {i18n.t("GuideMultiInput.flow_items_title") || "Flow Items"} ({items.length})
        </div>
        {items.length > 0 && (
          <button
            type="button"
            onClick={allCollapsed ? expandAll : collapseAll}
            style={{
              padding: "4px 10px",
              background: "transparent",
              border: "1px solid #e5e7eb",
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 600,
              color: "#6b7280",
              cursor: "pointer",
            }}
          >
            {allCollapsed
              ? i18n.t("GuideMultiInput.expand_all") || "Expand all"
              : i18n.t("GuideMultiInput.collapse_all") || "Collapse all"}
          </button>
        )}
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          onClick={() => addItem("click")}
          style={{
            flex: 1,
            padding: "6px 8px",
            background: "#fff",
            border: "1px solid #ff00a0",
            color: "#ff00a0",
            borderRadius: 6,
            cursor: "pointer",
            fontWeight: 600,
            fontSize: 12,
          }}
        >
          + {i18n.t("GuideMultiInput.add_click") || "Add Click"}
        </button>
        <button
          type="button"
          onClick={() => addItem("input")}
          style={{
            flex: 1,
            padding: "6px 8px",
            background: "#fff",
            border: "1px solid #3b82f6",
            color: "#3b82f6",
            borderRadius: 6,
            cursor: "pointer",
            fontWeight: 600,
            fontSize: 12,
          }}
        >
          + {i18n.t("GuideMultiInput.add_input") || "Add Input"}
        </button>
        <button
          type="button"
          onClick={() => addItem("tag")}
          style={{
            flex: 1,
            padding: "6px 8px",
            background: "#fff",
            border: "1px solid #a855f7",
            color: "#a855f7",
            borderRadius: 6,
            cursor: "pointer",
            fontWeight: 600,
            fontSize: 12,
          }}
        >
          + {i18n.t("GuideMultiInput.add_tag") || "Add Tag"}
        </button>
      </div>

      <div style={{ display: "flex", flexDirection: "column" }}>
        {items.length === 0 && (
          <div
            style={{
              padding: 16,
              textAlign: "center",
              color: "#9ca3af",
              fontSize: 12,
              border: "1px dashed #e5e7eb",
              borderRadius: 6,
            }}
          >
            {i18n.t("GuideMultiInput.empty_items") ||
              "No items yet. Add click, input, or tag items using the buttons above."}
          </div>
        )}
        {items.map((item, idx) => (
          <MultiInputItemEditor
            key={item.id || idx}
            item={item}
            index={idx}
            total={items.length}
            collapsed={!!collapsedMap[item.id]}
            onToggleCollapse={() => toggleItem(item.id)}
            onChange={(updated) => onItemChange(idx, updated)}
            onDelete={() => onDelete(idx)}
            onMoveUp={() => onMoveUp(idx)}
            onMoveDown={() => onMoveDown(idx)}
            siblingScenes={siblings}
            navItemsById={navItemsById}
            viewToolbarsById={viewToolbarsById}
          />
        ))}
      </div>
    </div>
  );
}
